import React, { useCallback } from 'react'
import { Button, IconButton } from '@mui/material'
import {
  Button as ContentfulButtonType,
  ButtonRole,
} from '@talentinc/gatsby-theme-ecom/types/page'
import { Modal as ModalType } from '@talentinc/gatsby-theme-ecom/types/modal'
import useEvents from '@talentinc/gatsby-theme-ecom/hooks/useEvents'
import { ModalVariants } from '@talentinc/gatsby-theme-ecom/types/modal'
import useUrlPathBuilder from '@talentinc/gatsby-theme-ecom/hooks/useUrlPathBuilder'
import useGTM from '@elgorditosalsero/react-gtm-hook'
import useLeadActions from '@talentinc/gatsby-theme-ecom/hooks/useLeadActions'
import { navigate } from 'gatsby'

import useMultiStepModal, { MultiStepModalProvider } from './useMultiStepModal'
import ContentfulModal from './ContentfulModal'
import { ConnectedResumeUploadButton } from '../Buttons/ResumeUpload'
import { useMixpanelCTAClickTracking } from '../../utils/mixpanel'
import { MixpanelCTALinkActions } from '../../types/mixpanel'

interface Props {
  button: ContentfulButtonType<ModalType>
  className?: string
  onClick?: React.MouseEventHandler | (() => void)
}

const ModalLaunchButton: React.FC<Props> = (props) => {
  const sendEvent = useEvents()
  const { sendDataToGTM } = useGTM()
  const { onClick } = props
  const {
    contentful_id: modalID,
    steps = [],
    variant,
  } = props.button.action || {}
  const { clearState } = useLeadActions()
  const { urlPathBuilder } = useUrlPathBuilder()

  const onOpen = useCallback<React.MouseEventHandler>(
    (event) => {
      onClick?.(event)

      sendEvent({
        event: 'Contentful Modal Open',
        variables: {
          contentful: {
            modalID,
            stepCount: steps.length,
            modalVariant: variant,
          },
        },
      })
    },
    [modalID, steps.length, variant, sendEvent, onClick]
  )

  const onClose = useCallback(
    (currentStep: number) => {
      // If there are errors, when you close it, they will disappear, which
      // isn't as smooth as it could be if we waited.
      setTimeout(() => clearState(), 500)
      sendEvent({
        event: 'Contentful Modal Close',
        variables: {
          contentful: {
            modalID,
            currentStep: currentStep + 1,
            stepCount: steps.length,
            modalVariant: variant,
          },
        },
      })
    },
    [modalID, steps.length, variant, sendEvent, clearState]
  )

  const onAdvanceStep = useCallback(
    (currentStep: number) => {
      const modalCompleted =
        steps.length === currentStep + 1 && steps.length > 1

      if (modalCompleted && variant === ModalVariants.ResumeUpload) {
        sendDataToGTM({
          event: 'Resume Uploaded',
          contentfulID: modalID,
        })
      }

      if (modalCompleted && props.button.action?.completionRedirect) {
        navigate(urlPathBuilder([props.button.action.completionRedirect.slug]))
      }

      sendEvent({
        event: modalCompleted
          ? 'Contentful Modal Completion'
          : 'Contentful Modal Step Advancement',
        variables: {
          contentful: {
            modalID,
            currentStep: currentStep + 1,
            stepCount: steps.length,
            modalVariant: variant,
          },
        },
      })
    },
    [
      modalID,
      steps.length,
      variant,
      sendEvent,
      sendDataToGTM,
      props.button.action,
      urlPathBuilder,
    ]
  )

  return (
    <MultiStepModalProvider
      onOpen={onOpen}
      onClose={onClose}
      onAdvanceStep={onAdvanceStep}
    >
      <InnerModalLaunchButton {...props} />
    </MultiStepModalProvider>
  )
}

const InnerModalLaunchButton: React.FC<Props> = ({ button, className }) => {
  const { handleOpen } = useMultiStepModal()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()

  if (!button.action?.__typename) {
    return null
  }

  if (button.role === ButtonRole.ResumeUpload) {
    return (
      <InnerModalResumeUploadLaunchButton
        button={button}
        className={className}
      />
    )
  }

  if (button.role === ButtonRole.LanguageSwitcher) {
    return (
      <>
        <IconButton
          id="regionsBtn"
          color={button.color || 'primary'}
          onClick={handleOpen}
          data-qa-modal-button={button.variant}
          className={className}
          size="large"
        >
          <img
            src="https://images.ctfassets.net/7thvzrs93dvf/smOV5DZw7SBuuhdNSpjUK/c99ff87339f6069a989da84a0afa8605/language.svg"
            alt="language_button"
            width={20}
            height={20}
          />
        </IconButton>
        <ContentfulModal {...button.action} />
      </>
    )
  }

  return (
    <>
      <Button
        variant={button.variant ?? 'contained'}
        color={button.color ?? 'primary'}
        onClick={(e) => {
          handleOpen(e)

          handleMpCTAClickTracking(
            e.currentTarget.textContent,
            MixpanelCTALinkActions.ResumeUploadAction
          )
        }}
        data-qa-modal-button={button.variant}
        className={className}
      >
        {button.buttonText}
      </Button>
      <ContentfulModal {...button.action} />
    </>
  )
}

const InnerModalResumeUploadLaunchButton: React.FC<Props> = ({
  button,
  className,
}) => {
  const { handleOpen } = useMultiStepModal()

  if (!button.action?.__typename) {
    return null
  }

  return (
    <>
      <ConnectedResumeUploadButton
        onFileSelect={handleOpen}
        uploadClass={className}
      >
        {button.buttonText}
      </ConnectedResumeUploadButton>
      <ContentfulModal {...button.action} />
    </>
  )
}

export default ModalLaunchButton
